import { Controller } from "@hotwired/stimulus";
import { httpRequest } from "../http";

//  Controller to show a loading overlay while calculation is in progress.
//  This keeps the user on the same page with a non-interactive overlay.

export default class extends Controller {
  static values = {
    statusUrl: String,
    calculating: Boolean,
    pollInterval: { type: Number, default: 2000 },
  };

  static targets = ["overlay", "content"];

  connect() {
    if (this.calculatingValue) {
      this.showOverlayImmediately();
      this.startPolling();
    }
  }

  disconnect() {
    this.stopPolling();
  }

  // Called when form is submitted or navigation link is clicked
  showOverlay() {
    this.showOverlayImmediately();
  }

  showOverlayImmediately() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden");
      this.overlayTarget.style.display = "flex";
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.add("pointer-events-none", "opacity-50");
    }
  }

  hideOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("hidden");
      this.overlayTarget.style.display = "none";
    }
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove("pointer-events-none", "opacity-50");
    }
  }

  startPolling() {
    this.pollTimer = setInterval(() => {
      this.fetchStatus();
    }, this.pollIntervalValue);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  fetchStatus() {
    // Avoid conditional GET (304): fetch treats 304 as !ok, so httpRequest throws and the overlay never clears.
    httpRequest(this.statusUrlValue, { method: "GET", cache: "no-store" })
      .then((response) => response.json())
      .then((data) => {
        this.handleStatusResponse(data);
      })
      .catch((error) => {
        console.error("Error fetching calculation status:", error);
      });
  }

  handleStatusResponse(data) {
    switch (data.status) {
      case "calculated":
        this.stopPolling();
        // Reload the page to show the calculated results
        window.location.reload();
        break;
      case "failed":
        this.stopPolling();
        this.showError();
        break;
      case "calculating":
      case "pending":
        // Continue polling
        break;
      default:
        console.warn("Unknown calculation status:", data.status);
    }
  }

  showError() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.innerHTML = `
        <div class="text-center max-w-md p-8">
          <div class="mb-6">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-error-100">
              <svg class="h-8 w-8 text-error-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <h2 class="text-xl font-bold text-gray-900 mb-2">Calculation failed</h2>
          <p class="text-gray-600 mb-4">Something went wrong while calculating wage gaps. Please try again.</p>
          <button onclick="window.location.reload()" class="btn-brand">
            Try Again
          </button>
        </div>
      `;
    }
  }

}
